import { useState } from 'react';
import { Download, Printer } from 'lucide-react';
import { Button } from './button';
import { toast } from './toast';
import type { ExportTable } from '../lib/export-table';
import { exportButtonsVisibility } from '../lib/export-visibility';

/**
 * Perm CODES this instance gates on. The component owns no codes of its own:
 * the admin's report surfaces pass the global `export.excel` / `export.pdf`,
 * while the cashier's Consignment tabs pass `CONSIGNMENT_PERMS.export` for BOTH
 * formats (grandfathered — a tenant that already narrowed consignment exports
 * must not be re-opened by the per-format rollout). All of them are ADVISORY:
 * the files are produced client-side from data the caller already holds, so
 * these hide affordances and are not a security boundary.
 *
 * Excel and PDF are separate codes because "may take a spreadsheet out" and
 * "may take a printable document out" are the two things the client asked to
 * control independently.
 */
export type ExportButtonsPerms = {
  excel: string;
  pdf: string;
  /** Only consulted when `onPrint` is passed; a surface with no printable
   *  document has no print authority to name. */
  print?: string;
};

export type ExportButtonsProps = {
  table: () => ExportTable | null;
  /** Only tabs with a printable document pass this. */
  onPrint?: () => void;
  disabled?: boolean;
  /** Caveat shown beside the buttons and in the file, e.g. the 200-doc cap. */
  note?: string;
  perms: ExportButtonsPerms;
  /**
   * The host app's reactive perm hook (`useHasPerm` from its own auth store) —
   * `@mlm/ui` has no session of its own and must not reach into an app.
   *
   * Pass a MODULE-LEVEL function, never an inline closure: it is called as a
   * hook, so its identity has to be stable for the life of the mounted tree.
   * Apps wire this once in a thin binding component rather than at every call
   * site.
   */
  useHasPerm: (perm: string) => boolean;
};

/**
 * Print / Export to Excel / Export to PDF for one table surface.
 *
 * `table` is a THUNK so the export snapshots the data (and the generation
 * timestamp) at click time rather than at render time.
 *
 * The two writers are `import()`-ed on click, so jsPDF and the zip writer sit
 * in their own async chunks and never touch the host app's initial bundle —
 * which is also why this module is NOT re-exported from the `@mlm/ui` barrel.
 * No pop-up shell is involved — unlike the print path, an anchor-driven
 * download still works after an awaited chunk load.
 */
export function ExportButtons({
  table,
  onPrint,
  disabled,
  note,
  perms,
  useHasPerm,
}: ExportButtonsProps) {
  // Three UNCONDITIONAL hook calls whatever the perms object holds (hook order
  // must not depend on data) — `''` is never a granted code, so a surface that
  // names no print authority simply reads as "no".
  const canExcel = useHasPerm(perms.excel);
  const canPdf = useHasPerm(perms.pdf);
  const canPrint = useHasPerm(perms.print ?? '');
  const { showExcel, showPdf, showPrint } = exportButtonsVisibility({
    canExcel,
    canPdf,
    hasPrint: !!onPrint && canPrint,
  });
  const [busy, setBusy] = useState<null | 'xlsx' | 'pdf'>(null);

  const run = async (kind: 'xlsx' | 'pdf') => {
    const t = table();
    if (!t) return;
    setBusy(kind);
    try {
      const filename =
        kind === 'xlsx'
          ? (await import('../lib/export-xlsx')).downloadTableXlsx(t)
          : (await import('../lib/export-pdf')).downloadTablePdf(t);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error('Export failed — please try again.');
    } finally {
      setBusy(null);
    }
  };

  if (!showExcel && !showPdf && !showPrint) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {note && (showExcel || showPdf) ? (
        <span className="text-xs text-muted-foreground">{note}</span>
      ) : null}
      {showPrint && (
        <Button size="sm" variant="outline" className="gap-1" onClick={onPrint} disabled={disabled}>
          <Printer className="h-4 w-4" /> Print
        </Button>
      )}
      {showExcel && (
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          title={note}
          disabled={disabled || busy !== null}
          onClick={() => void run('xlsx')}
        >
          <Download className="h-4 w-4" />
          {busy === 'xlsx' ? 'Exporting…' : 'Export to Excel'}
        </Button>
      )}
      {showPdf && (
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          title={note}
          disabled={disabled || busy !== null}
          onClick={() => void run('pdf')}
        >
          <Download className="h-4 w-4" />
          {busy === 'pdf' ? 'Exporting…' : 'Export to PDF'}
        </Button>
      )}
    </div>
  );
}
